import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';

import { DsPanel } from 'src/component/ds/panel/DsPanel';
import { Button, type ButtonProps } from 'src/component/ds/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'src/component/ds/ui/dropdownMenu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import { hasFeaturePermissions, hasPermissions } from 'src/module/authentication/selector';
import useIsOnPremDeployment from 'src/hooks/useIsOnPremDeployment';
import { exportDeviceList } from 'src/module/deviceList/action';
import { getDeviceListExporting } from 'src/module/deviceList/selector';
import DeviceListSearch from './DeviceListSearch';

const ICON_SX = { fontSize: 16 };

type Blade = 'addDevice' | 'importDevices';

interface ToolbarActionProps extends ButtonProps {
  label: string;
}

const ToolbarAction = React.forwardRef<HTMLButtonElement, ToolbarActionProps>(
  ({ label, variant = 'secondary', ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button ref={ref} variant={variant} size='icon' aria-label={label} {...props} />
      </TooltipTrigger>
      <TooltipContent side='bottom'>{label}</TooltipContent>
    </Tooltip>
  )
);
ToolbarAction.displayName = 'ToolbarAction';

export interface DeviceListToolbarProps {
  search: string;
  onSearchChange: (search: string) => void;
  className?: string;
}

export default function DeviceListToolbar ({ search, onSearchChange, className }: DeviceListToolbarProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOnPrem = useIsOnPremDeployment();
  const exporting = useSelector(getDeviceListExporting);
  const [blade, setBlade] = useState<Blade | null>(null);

  const canBatchSchedule = useSelector(hasPermissions('read-batch-schedule'));
  const canEditDevice = useSelector(hasPermissions('edit-device'));
  const hasBatchFeatures = useSelector(hasFeaturePermissions(['batches-and-recipes', 'program-device']));
  const hasReplaceFeatures = useSelector(hasFeaturePermissions(['replace-device', 'program-device']));
  const hasCircuitManagement = useSelector(hasFeaturePermissions('circuit-management'));

  const showBatchUpdate = canBatchSchedule && hasBatchFeatures;
  // Cloud only, so hidden until the deployment type is known.
  const showReplaceDevice = isOnPrem === false && canEditDevice && hasReplaceFeatures;
  const showAddDevice = canEditDevice;
  const showImportDevices = canEditDevice && hasCircuitManagement;

  return (
    <div className={cn('ds-flex ds-items-center ds-gap-4', className)}>
      <DeviceListSearch value={search} onChange={onSearchChange} className='ds-w-full ds-min-w-0 ds-max-w-[295px]' />

      <TooltipProvider delayDuration={200}>
        <div role='group' aria-label={t('deviceList.actions.label')} className='ds-ml-auto ds-flex ds-shrink-0 ds-items-center ds-gap-3'>
          {showBatchUpdate && (
            <ToolbarAction label={t('deviceList.actions.batchUpdate')} asChild>
              <Link to='/program/list'><EditOutlinedIcon sx={ICON_SX} aria-hidden='true' /></Link>
            </ToolbarAction>
          )}
          {showReplaceDevice && (
            <ToolbarAction label={t('deviceList.actions.replaceDevice')} asChild>
              <Link to='/devices/upgrade'><SwapHorizOutlinedIcon sx={ICON_SX} aria-hidden='true' /></Link>
            </ToolbarAction>
          )}
          <ToolbarAction
            label={t('deviceList.actions.exportList')}
            disabled={exporting}
            aria-busy={exporting}
            onClick={() => dispatch(exportDeviceList())}
          >
            <FileDownloadOutlinedIcon sx={ICON_SX} aria-hidden='true' />
          </ToolbarAction>
          {(showAddDevice || showImportDevices) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarAction label={t('deviceList.actions.add')} variant='primary'>
                  <AddIcon sx={ICON_SX} aria-hidden='true' />
                </ToolbarAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {showAddDevice && (
                  <DropdownMenuItem onSelect={() => setBlade('addDevice')}>
                    <AddCircleOutlineIcon aria-hidden='true' />
                    {t('deviceList.actions.addDevice')}
                  </DropdownMenuItem>
                )}
                {showImportDevices && (
                  <DropdownMenuItem onSelect={() => setBlade('importDevices')}>
                    <FileUploadOutlinedIcon aria-hidden='true' />
                    {t('deviceList.actions.importDeviceList')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TooltipProvider>

      {/* Empty until FOX-2710 builds the add and import forms. */}
      <DsPanel
        open={blade !== null}
        onOpenChange={(open) => { if (!open) setBlade(null); }}
        title={blade === 'importDevices' ? t('deviceList.actions.importDeviceList') : t('deviceList.actions.addDevice')}
      >
        {null}
      </DsPanel>
    </div>
  );
}
